<script setup lang="ts">
/**
 * What the Financial Administration has, and what it has not.
 *
 * THE HONEST SCREEN. A Playground acknowledgement discharges no obligation, so
 * a sale that came back with one is shown here as an exception rather than in a
 * reported column — it is the one failure that looks exactly like success, and
 * a green tick over it would be the single most dangerous pixel in the product.
 *
 * The numbering half sits alongside because the two answer one question
 * together: gaps say which numbers were leased and never spent, the register
 * says which were spent and on what. An auditor asking "show me your receipts
 * are gapless" is asking for both.
 */

import StatTile from "./StatTile.vue";
import type { FiscalState } from "../api";
import { clockTime, money } from "../format";

const props = defineProps<{
  fiscal: FiscalState;
  gaps: readonly Record<string, unknown>[];
  issued: number;
  timezone: string;
  locale: string;
  t: (key: string) => string;
}>();

/**
 * SAY WHEN THE LIST IS CUT SHORT.
 *
 * These lists cap at twenty so a venue with six hundred unreported sales does
 * not render six hundred rows. A cap with nothing saying so reads as "there
 * are twenty", which on THIS screen is the difference between a nuisance and a
 * breach — and the tiles above already carry the true count, so a reader who
 * notices the mismatch learns only that one of the two is lying.
 */
const SHOWN = 20;
/** Rows shown before the fold, as evidence of the sentence above them. */
const PEEK = 3;
const more = (rows: readonly unknown[]): number => Math.max(0, rows.length - SHOWN);

/**
 * A ROW SOMEBODY CAN ACT ON.
 *
 * This list used to be a check id and a POK, which identifies the row in our
 * database and nothing in the world. The person chasing an unreported sale is
 * talking to the Financial Administration or holding a guest's receipt, and
 * both of those name it by NUMBER and AMOUNT. The time is here because "which
 * one?" is usually answered by when it happened.
 *
 * The check id stays, last and quiet, because a support conversation with us
 * does need it — but it is no longer the only thing on the line.
 */
const label = (row: Record<string, unknown>): string => {
  const parts: string[] = [];

  const number = row["receiptNumber"];
  if (typeof number === "string" && number.length > 0) parts.push(number);

  const total = row["totalMinor"];
  // Minor units as a decimal string on the wire; `money` wants the bigint.
  if (typeof total === "string") parts.push(money(BigInt(total), props.locale));

  const at = row["saleAt"];
  if (typeof at === "string") parts.push(clockTime(at, props.timezone, props.locale));

  // Nothing readable came back, so show the id rather than an empty line: a
  // submission whose payload cannot be read is the worst row here, not one to
  // quietly skip.
  if (parts.length === 0) return String(row["checkId"] ?? "");
  return parts.join(" · ");
};
</script>

<template>
  <section>
    <div class="gf-tiles">
      <StatTile
        :label="t('fiscal.credentials')"
        :value="fiscal.credentials === 'present' ? t('fiscal.present') : t('fiscal.absent')"
        :tone="fiscal.credentials === 'present' ? 'plain' : 'bad'"
        :hint="fiscal.credentials === 'present' ? undefined : t('fiscal.absentWhy')"
      />
      <StatTile
        :label="t('fiscal.playground')"
        :value="String(fiscal.playground.length)"
        :tone="fiscal.playground.length > 0 ? 'warn' : 'plain'"
        :hint="fiscal.playground.length > 0 ? t('fiscal.playgroundWhy') : undefined"
      />
      <StatTile
        :label="t('fiscal.unreportable')"
        :value="String(fiscal.unreportable.length)"
        :tone="fiscal.unreportable.length > 0 ? 'warn' : 'plain'"
      />
      <StatTile
        :label="t('fiscal.quarantined')"
        :value="String(fiscal.quarantined.length)"
        :tone="fiscal.quarantined.length > 0 ? 'bad' : 'plain'"
      />
      <StatTile :label="t('fiscal.issued')" :value="String(issued)" />
      <StatTile
        :label="t('fiscal.gaps')"
        :value="String(gaps.length)"
        :tone="gaps.length > 0 ? 'warn' : 'plain'"
      />
    </div>

    <div
      v-if="fiscal.playground.length > 0"
      class="gf-panel gf-panel--warn"
      role="alert"
    >
      <h2 class="gf-panel__title">{{ t("fiscal.playground") }}</h2>
      <p class="gf-note">{{ t("fiscal.playgroundWhy") }}</p>
      <!--
        Three as evidence, the rest folded. A demo venue has hundreds of these
        and they filled the screen, pushing everything below out of reach; the
        sentence above is the news and a few rows are proof of it.
      -->
      <ul class="gf-list">
        <li v-for="(row, i) in fiscal.playground.slice(0, PEEK)" :key="i">
          {{ label(row) }}
          <span class="gf-muted gf-mono">{{ String(row["pok"] ?? "") }}</span>
        </li>
      </ul>
      <details v-if="fiscal.playground.length > PEEK" class="fiscal__rows">
        <summary class="fiscal__summary">
          {{ t("fiscal.showRows") }} · {{ fiscal.playground.length - PEEK }}
        </summary>
        <ul class="gf-list">
          <li v-for="(row, i) in fiscal.playground.slice(PEEK, SHOWN)" :key="i">
            {{ label(row) }}
            <span class="gf-muted gf-mono">{{ String(row["pok"] ?? "") }}</span>
          </li>
        </ul>
        <p v-if="more(fiscal.playground) > 0" class="gf-quiet">
          {{ t("fiscal.andMore") }} {{ more(fiscal.playground) }}
        </p>
      </details>
    </div>

    <!--
      REFUSED BY THE AUTHORITY, and until now a number on a tile.

      "Quarantined: 3" tells somebody a problem exists and gives them no way to
      begin. These are the rows where the Financial Administration said no, so
      the code and the message it gave are the whole point of the line.
    -->
    <div v-if="fiscal.quarantined.length > 0" class="gf-panel gf-panel--warn" role="alert">
      <h2 class="gf-panel__title">{{ t("fiscal.quarantined") }}</h2>
      <ul class="gf-list gf-list--warn">
        <li v-for="(row, i) in fiscal.quarantined.slice(0, SHOWN)" :key="i">
          {{ label(row) }}
          <span class="gf-muted">
            {{ String(row["code"] ?? "") }} {{ String(row["message"] ?? "") }}
            · {{ t("fiscal.attempts") }} {{ String(row["attempts"] ?? "") }}
          </span>
        </li>
      </ul>
      <p v-if="more(fiscal.quarantined) > 0" class="gf-quiet">
        {{ t("fiscal.andMore") }} {{ more(fiscal.quarantined) }}
      </p>
    </div>

    <!--
      THE WORST CASE: a settled check that never became a submission at all, so
      it is in no queue and nothing will retry it. D11 says giving up must be
      visible, and a count on a tile is not visible enough.
    -->
    <div v-if="fiscal.unreportable.length > 0" class="gf-panel gf-panel--warn" role="alert">
      <h2 class="gf-panel__title">{{ t("fiscal.unreportable") }}</h2>
      <ul class="gf-list gf-list--warn">
        <li v-for="(row, i) in fiscal.unreportable.slice(0, SHOWN)" :key="i">
          <span class="gf-mono">{{ String(row["checkId"] ?? "") }}</span>
          <span class="gf-muted">
            {{ t(`fiscal.kind.${String(row["kind"] ?? "")}`) }} — {{ String(row["detail"] ?? "") }}
          </span>
        </li>
      </ul>
      <p v-if="more(fiscal.unreportable) > 0" class="gf-quiet">
        {{ t("fiscal.andMore") }} {{ more(fiscal.unreportable) }}
      </p>
    </div>

    <div v-if="fiscal.oldestPendingSaleAt" class="gf-panel">
      <h2 class="gf-panel__title">{{ t("fiscal.oldestPending") }}</h2>
      <p class="gf-mono">{{ fiscal.oldestPendingSaleAt }}</p>
    </div>

    <div class="gf-panel">
      <h2 class="gf-panel__title">{{ t("fiscal.numbering") }}</h2>
      <p v-if="gaps.length === 0" class="gf-quiet">{{ t("fiscal.gapsClean") }}</p>
      <ul v-else class="gf-list gf-list--warn">
        <li v-for="(gap, i) in gaps" :key="i" class="gf-mono">
          {{ String(gap["fromNumber"] ?? "") }}–{{ String(gap["toNumber"] ?? "") }}
          <span class="gf-muted">{{ String(gap["cause"] ?? "") }}</span>
        </li>
      </ul>
    </div>

    <p
      v-if="
        fiscal.credentials === 'present' &&
        fiscal.playground.length === 0 &&
        fiscal.unreportable.length === 0 &&
        fiscal.quarantined.length === 0
      "
      class="gf-quiet"
    >
      {{ t("fiscal.clean") }}
    </p>
  </section>
</template>

<style scoped>
.fiscal__summary {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  font-weight: var(--gf-weight-medium);
  cursor: pointer;
}

.fiscal__summary:focus-visible {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: var(--gf-focus-offset);
}
</style>
